/**
 * React Query hooks for MyParksValue.com Valuation Requests
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getValuationRequests,
  getArchivedValuationRequests,
  getValuationRequestCount,
  updateValuationRequest,
  archiveValuationRequest,
  restoreValuationRequest,
  getAssessmentsByEmail,
} from '@/services/valuation-request.service';
import type { ValuationRequestUpdate } from '@/types/valuation-request';

export const valuationRequestKeys = {
  all: ['valuation-requests'] as const,
  list: () => [...valuationRequestKeys.all, 'list'] as const,
  archived: () => [...valuationRequestKeys.all, 'archived'] as const,
  count: () => [...valuationRequestKeys.all, 'count'] as const,
  assessments: (email: string) => [...valuationRequestKeys.all, 'assessments', email] as const,
};

export function useValuationRequests() {
  return useQuery({
    queryKey: valuationRequestKeys.list(),
    queryFn: getValuationRequests,
    staleTime: 30_000,
  });
}

export function useArchivedValuationRequests() {
  return useQuery({
    queryKey: valuationRequestKeys.archived(),
    queryFn: getArchivedValuationRequests,
    staleTime: 30_000,
  });
}

export function useValuationRequestCount() {
  return useQuery({
    queryKey: valuationRequestKeys.count(),
    queryFn: getValuationRequestCount,
    staleTime: 60_000,
  });
}

export function useUpdateValuationRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: ValuationRequestUpdate }) =>
      updateValuationRequest(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuationRequestKeys.list() });
    },
  });
}

export function useArchiveValuationRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archivedBy }: { id: string; archivedBy?: string }) =>
      archiveValuationRequest(id, archivedBy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuationRequestKeys.all });
    },
  });
}

export function useRestoreValuationRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreValuationRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: valuationRequestKeys.all });
    },
  });
}

export function useAssessmentsByEmail(email: string | null | undefined) {
  return useQuery({
    queryKey: valuationRequestKeys.assessments(email || ''),
    queryFn: () => getAssessmentsByEmail(email!),
    enabled: !!email,
    staleTime: 60_000,
  });
}
